/**
 * Phase 17.3 — Unified Campaign Builder Page.
 *
 * Single wizard with three independent dial groups:
 *   • Variance dimensions (multi-select toggles)
 *   • Sampling strategy (single-select)
 *   • Analysis goals (multi-select)
 *
 * Replaces the legacy NewCampaignWizardPage. Old `/campaigns/new`
 * route resolves here.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, AlertTriangle, AlertCircle, Info, CheckCircle2 } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import ICBoundsSection from "@/components/campaigns/sections/ICBoundsSection";
import ScenarioPickerCard from "@/components/optimisation/BaselineScenarioPicker";
import unifiedCampaigns from "@/api/unifiedCampaigns";
import pkCampaigns from "@/api/pkCampaigns";
import {
    validateCampaign, reconcileGaInvariants, FieldBounds,
} from "@/lib/unifiedCampaignSchema";

const DEFAULT_TARGET_BOUNDS = {
    x_m: { lo: -3000, hi: 3000 },
    y_m: { lo: 500, hi: 4000 },
    z_m: { lo: 100, hi: 2500 },
};
const DEFAULT_INTERCEPTOR_BOUNDS = {
    x_m: { lo: 0, hi: 0 },
    y_m: { lo: 0, hi: 0 },
    z_m: { lo: 50, hi: 50 },
};

export default function UnifiedCampaignBuilderPage() {
    const navigate = useNavigate();
    const [name, setName] = useState("New unified campaign");
    const [description, setDescription] = useState("");
    const [scenarioId, setScenarioId] = useState("");
    const [targetEnabled, setTargetEnabled] = useState(false);
    const [interEnabled, setInterEnabled] = useState(false);
    const [targetBounds, setTargetBounds] = useState(DEFAULT_TARGET_BOUNDS);
    const [interBounds, setInterBounds] = useState(DEFAULT_INTERCEPTOR_BOUNDS);
    const [headingEnabled, setHeadingEnabled] = useState(false);
    const [headingLo, setHeadingLo] = useState(-45);
    const [headingHi, setHeadingHi] = useState(45);
    const [sensorDeg, setSensorDeg] = useState(false);
    const [fuelMargin, setFuelMargin] = useState(false);
    const [aeroNoise, setAeroNoise] = useState(false);
    const [gainSearch, setGainSearch] = useState(false);
    const [samplerKind, setSamplerKind] = useState("lhs");
    const [nSamples, setNSamples] = useState(50);
    const [mcSeeds, setMcSeeds] = useState(1);
    const [seed, setSeed] = useState(42);
    // Phase 17.7 — restored Phase 15.1 GA defaults
    const [gaPop, setGaPop] = useState(32);
    const [gaGens, setGaGens] = useState(15);
    const [gaMc, setGaMc] = useState(20);
    const [gaTournament, setGaTournament] = useState(3);
    const [gaCrossover, setGaCrossover] = useState(0.7);
    const [gaMutation, setGaMutation] = useState(0.15);
    const [gaElitism, setGaElitism] = useState(2);
    const [gaEarlyStop, setGaEarlyStop] = useState(5);
    // Phase 17.7 — perturbation preset (33-var registry)
    const [perturbationPreset, setPerturbationPreset] = useState("nominal");
    const [perturbationOverridesJson, setPerturbationOverridesJson] = useState("");
    // Phase 17.7 — polar geometry envelope
    const [envelopeEnabled, setEnvelopeEnabled] = useState(false);
    const [envRangeLo, setEnvRangeLo] = useState(800);
    const [envRangeHi, setEnvRangeHi] = useState(4500);
    const [envBearingLo, setEnvBearingLo] = useState(-180);
    const [envBearingHi, setEnvBearingHi] = useState(180);
    const [envAltLo, setEnvAltLo] = useState(100);
    const [envAltHi, setEnvAltHi] = useState(2000);
    const [envApproachLo, setEnvApproachLo] = useState(-90);
    const [envApproachHi, setEnvApproachHi] = useState(90);
    const [envSampler, setEnvSampler] = useState("lhs");
    // Phase 17.7 — degradation profile attach (Phase 14)
    const [degradationProfileId, setDegradationProfileId] = useState("");
    const [profilesList, setProfilesList] = useState([]);
    // Phase 17.7 — sensitivity knobs (Phase 11)
    const [sensitivityMethod, setSensitivityMethod] = useState("variance_first_order");
    const [sensitivitySamplesPerVar, setSensitivitySamplesPerVar] = useState(20);
    // Phase 17.7 — target-Pk knobs (Phase 12)
    const [targetPkThreshold, setTargetPkThreshold] = useState(0.7);
    const [targetPkLambda, setTargetPkLambda] = useState(0.1);
    const [targetPkMaxIter, setTargetPkMaxIter] = useState(30);
    const [targetPkSamplesPerIter, setTargetPkSamplesPerIter] = useState(20);
    // Phase 17.9c — variable subset (sensitivity) + variable budget (target-Pk)
    // chip multi-selects, restored from the legacy SensitivitySection /
    // TargetPkSection patterns. Both default to all 33 registry IDs once
    // the registry has loaded and the operator first toggles the goal on.
    const [registryIds, setRegistryIds] = useState([]);
    const [registryLoading, setRegistryLoading] = useState(false);
    const [registryError, setRegistryError] = useState(null);
    const [sensitivityVariableSubset, setSensitivityVariableSubset] = useState([]);
    const [targetPkVariableBudget, setTargetPkVariableBudget] = useState([]);
    // Phase 18.C — per-variable inline range overrides for the
    // sensitivity subset chips. Shape: { var_id: {low: number, high: number} }
    // Empty / missing entries fall back to the platform's preset default.
    const [perturbationOverrides, setPerturbationOverrides] = useState({});
    const [goalDescriptive, setGoalDescriptive] = useState(true);
    const [goalSensitivity, setGoalSensitivity] = useState(false);
    const [goalEnvelope, setGoalEnvelope] = useState(false);
    const [goalTargetPk, setGoalTargetPk] = useState(false);
    const [goalGA, setGoalGA] = useState(false);
    // Phase 17.7 UX hotfix — last auto-fix hint to surface alongside the
    // toggle that triggered the cross-field reconcile.
    const [lastAutoFix, setLastAutoFix] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const isGA = samplerKind === "ga";

    // Phase 17.7 UX hotfix — controlled handlers for the three fields
    // whose values are bound by `_cross_validate`. Each calls
    // `reconcileGaInvariants` so the operator can never park the form
    // in a state the backend will reject.
    const onGainSearchToggle = (v) => {
        const r = reconcileGaInvariants({
            gainSearch: v, samplerKind, gaGoal: goalGA, changed: "gainSearch",
        });
        setGainSearch(r.gainSearch);
        setSamplerKind(r.samplerKind);
        setGoalGA(r.gaGoal);
        if (r.hints[0]) { setLastAutoFix(r.hints[0]); toast.info(r.hints[0]); }
        else { setLastAutoFix(null); }
    };
    const onSamplerKindChange = (k) => {
        const r = reconcileGaInvariants({
            gainSearch, samplerKind: k, gaGoal: goalGA, changed: "samplerKind",
        });
        setSamplerKind(r.samplerKind);
        setGainSearch(r.gainSearch);
        setGoalGA(r.gaGoal);
        if (r.hints[0]) { setLastAutoFix(r.hints[0]); toast.info(r.hints[0]); }
        else { setLastAutoFix(null); }
    };
    const onGoalGaToggle = (v) => {
        const r = reconcileGaInvariants({
            gainSearch, samplerKind, gaGoal: v, changed: "gaGoal",
        });
        setGoalGA(r.gaGoal);
        setSamplerKind(r.samplerKind);
        setGainSearch(r.gainSearch);
        if (r.hints[0]) { setLastAutoFix(r.hints[0]); toast.info(r.hints[0]); }
        else { setLastAutoFix(null); }
    };
    const showSampleWarning = nSamples > 1000 && samplerKind !== "ga";

    // Phase 17.7 UX hotfix — build a synthetic payload mirroring the
    // backend submit body, then run cross-field validation on every
    // render. Result drives both the disabled-submit gate and the
    // inline "Validation summary" card.
    const builtPayload = useMemo(() => ({
        scenario_id: scenarioId,
        name, description,
        variance_dimensions: {
            target_ic_bounds: targetEnabled ? targetBounds : null,
            interceptor_ic_bounds: interEnabled ? interBounds : null,
            target_heading: headingEnabled
                ? { mode: "range", lo_deg: Number(headingLo), hi_deg: Number(headingHi) }
                : null,
            target_control_pattern: [],
            sensor_degradation: sensorDeg,
            fuel_margin: fuelMargin,
            aero_noise: aeroNoise,
            guidance_gain_search: gainSearch,
            perturbation_overrides: Object.fromEntries(
                Object.entries(perturbationOverrides)
                    .filter(([, v]) => v && Number.isFinite(Number(v.low)) && Number.isFinite(Number(v.high)))
                    .map(([k, v]) => [k, {
                        kind: "uniform",
                        params: { low: Number(v.low), high: Number(v.high) },
                    }]),
            ),
            perturbation_preset: perturbationPreset,
            geometry_envelope: envelopeEnabled ? {
                range_m: { lo: Number(envRangeLo), hi: Number(envRangeHi) },
                bearing_deg: { lo: Number(envBearingLo), hi: Number(envBearingHi) },
                altitude_m: { lo: Number(envAltLo), hi: Number(envAltHi) },
                approach_vector_deg: { lo: Number(envApproachLo), hi: Number(envApproachHi) },
                sampler_kind: envSampler,
                samples_per_point: 1,
            } : null,
            degradation_profile_id: degradationProfileId || null,
        },
        sampling_strategy: {
            kind: samplerKind,
            n_samples: Number(nSamples),
            mc_seeds_per_sample: Number(mcSeeds),
            seed: Number(seed),
            target_pk_threshold: Number(targetPkThreshold),
        },
        analysis_goals: {
            descriptive_pk: goalDescriptive,
            sensitivity_ranking: goalSensitivity,
            target_pk_search: goalTargetPk,
            envelope_heatmap: goalEnvelope,
            ga_optimisation_convergence: goalGA,
            adaptive_policy_training: false,
        },
    }), [
        scenarioId, name, description,
        targetEnabled, interEnabled, targetBounds, interBounds,
        headingEnabled, headingLo, headingHi,
        sensorDeg, fuelMargin, aeroNoise, gainSearch,
        perturbationPreset, envelopeEnabled, envRangeLo, envRangeHi,
        envBearingLo, envBearingHi, envAltLo, envAltHi,
        envApproachLo, envApproachHi, envSampler, degradationProfileId,
        samplerKind, nSamples, mcSeeds, seed, targetPkThreshold,
        goalDescriptive, goalSensitivity, goalTargetPk, goalEnvelope, goalGA,
        perturbationOverrides,
    ]);
    const validation = useMemo(() => validateCampaign(builtPayload), [builtPayload]);
    const hasErrors = validation.errors.length > 0;

    // Phase 17.7 — load attached degradation profiles list once.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const base = process.env.REACT_APP_BACKEND_URL || "";
                const tok = window?.localStorage?.getItem?.("uavsimnet_jwt");
                const r = await fetch(`${base}/api/degradation-profiles`, {
                    headers: tok ? { Authorization: `Bearer ${tok}` } : {},
                });
                if (!r.ok) return;
                const body = await r.json();
                if (cancelled) return;
                setProfilesList(Array.isArray(body?.items) ? body.items : (body || []));
            } catch {/* tolerate */}
        })();
        return () => { cancelled = true; };
    }, []);

    // Phase 17.9c — load the 33-variable perturbation registry once so the
    // subset / budget chip grids have a stable canonical ID list. The
    // registry shape matches the legacy ``pk-campaigns/registry`` payload
    // ({variables|entries|catalog}[].id).
    useEffect(() => {
        let cancelled = false;
        setRegistryLoading(true);
        (async () => {
            try {
                const body = await pkCampaigns.registry();
                if (cancelled) return;
                const arr = body?.variables || body?.entries || body?.catalog || [];
                const ids = Array.isArray(arr)
                    ? arr.map((v) => v?.id).filter(Boolean)
                    : [];
                setRegistryIds(ids);
                setRegistryError(null);
            } catch (e) {
                if (cancelled) return;
                setRegistryError(e?.message || "Failed to load perturbation registry");
            } finally {
                if (!cancelled) setRegistryLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // Phase 17.9c — wrapped goal toggles. When the operator toggles
    // sensitivity / target-Pk ON for the first time, pre-select all
    // registry IDs (matches PRD "default = all 33 selected"). When
    // toggling off, leave the array as-is so re-toggling preserves any
    // prior trim.
    const onGoalSensitivityToggle = (v) => {
        setGoalSensitivity(v);
        if (v && sensitivityVariableSubset.length === 0 && registryIds.length > 0) {
            setSensitivityVariableSubset(registryIds);
        }
    };
    const onGoalTargetPkToggle = (v) => {
        setGoalTargetPk(v);
        if (v && targetPkVariableBudget.length === 0 && registryIds.length > 0) {
            setTargetPkVariableBudget(registryIds);
        }
    };

    // Phase 17.9c — backfill defaults if the goal was toggled on BEFORE
    // the registry finished loading. Only runs once registry arrives.
    useEffect(() => {
        if (registryIds.length === 0) return;
        if (goalSensitivity && sensitivityVariableSubset.length === 0) {
            setSensitivityVariableSubset(registryIds);
        }
        if (goalTargetPk && targetPkVariableBudget.length === 0) {
            setTargetPkVariableBudget(registryIds);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [registryIds]);

    const toggleSensitivityVar = (id) => {
        setSensitivityVariableSubset((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
        );
    };
    const toggleTargetPkVar = (id) => {
        setTargetPkVariableBudget((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
        );
    };

    const summary = useMemo(() => {
        const v = [];
        if (targetEnabled) v.push("target IC");
        if (interEnabled) v.push("interceptor IC");
        if (headingEnabled) v.push("target heading");
        if (sensorDeg) v.push("sensor degradation");
        if (fuelMargin) v.push("fuel margin");
        if (aeroNoise) v.push("aero noise");
        if (gainSearch) v.push("guidance-gain search");
        return v.length ? v.join(" · ") : "(no variance — single fixed point)";
    }, [targetEnabled, interEnabled, headingEnabled, sensorDeg,
        fuelMargin, aeroNoise, gainSearch]);

    const submit = async () => {
        setBusy(true);
        setError(null);
        try {
            // Parse perturbation overrides JSON if provided.
            let overrides = {};
            if (perturbationOverridesJson.trim()) {
                try { overrides = JSON.parse(perturbationOverridesJson); }
                catch (e) { setError("Perturbation overrides JSON invalid"); setBusy(false); return; }
            }
            const body = {
                scenario_id: scenarioId,
                name,
                description,
                variance_dimensions: {
                    target_ic_bounds: targetEnabled ? targetBounds : null,
                    interceptor_ic_bounds: interEnabled ? interBounds : null,
                    target_heading: headingEnabled
                        ? { mode: "range", lo_deg: Number(headingLo), hi_deg: Number(headingHi) }
                        : null,
                    target_control_pattern: [],
                    sensor_degradation: sensorDeg,
                    fuel_margin: fuelMargin,
                    aero_noise: aeroNoise,
                    guidance_gain_search: gainSearch,
                    perturbation_overrides: overrides,
                    perturbation_preset: perturbationPreset,
                    geometry_envelope: envelopeEnabled ? {
                        range_m: { lo: Number(envRangeLo), hi: Number(envRangeHi) },
                        bearing_deg: { lo: Number(envBearingLo), hi: Number(envBearingHi) },
                        altitude_m: { lo: Number(envAltLo), hi: Number(envAltHi) },
                        approach_vector_deg: { lo: Number(envApproachLo), hi: Number(envApproachHi) },
                        sampler_kind: envSampler,
                        samples_per_point: 1,
                    } : null,
                    degradation_profile_id: degradationProfileId || null,
                },
                sampling_strategy: {
                    kind: samplerKind,
                    n_samples: Number(nSamples),
                    mc_seeds_per_sample: Number(mcSeeds),
                    seed: Number(seed),
                    ...(isGA ? {
                        ga_population_size: Number(gaPop),
                        ga_generations: Number(gaGens),
                        ga_mc_seeds_per_individual: Number(gaMc),
                        ga_max_workers: 4,
                        ga_tournament_size: Number(gaTournament),
                        ga_crossover_rate: Number(gaCrossover),
                        ga_mutation_rate: Number(gaMutation),
                        ga_elitism: Number(gaElitism),
                        ga_early_stop_no_improve: Number(gaEarlyStop),
                    } : {}),
                    ...(goalSensitivity ? {
                        sensitivity_method: sensitivityMethod,
                        sensitivity_samples_per_variable: Number(sensitivitySamplesPerVar),
                        sensitivity_variable_subset: sensitivityVariableSubset,
                    } : {}),
                    ...(goalTargetPk ? {
                        target_pk_threshold: Number(targetPkThreshold),
                        target_pk_lambda_penalty: Number(targetPkLambda),
                        target_pk_max_iterations: Number(targetPkMaxIter),
                        target_pk_samples_per_iteration: Number(targetPkSamplesPerIter),
                        target_pk_variable_budget: targetPkVariableBudget,
                    } : {}),
                },
                analysis_goals: {
                    descriptive_pk: goalDescriptive,
                    sensitivity_ranking: goalSensitivity,
                    target_pk_search: goalTargetPk,
                    envelope_heatmap: goalEnvelope,
                    ga_optimisation_convergence: goalGA,
                    adaptive_policy_training: false,
                },
            };
            const created = await unifiedCampaigns.create(body);
            toast.success(`Campaign created — ${samplerKind}`);
            navigate(`/campaigns/${created.id}`);
        } catch (e) {
            setError(
                e?.response?.data?.detail
                    ? JSON.stringify(e.response.data.detail)
                    : (e?.message || "Submit failed"),
            );
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="unified-campaign-builder-page">
            <PageHeader
                title="Unified Campaign Workbench"
                subtitle="Three independent dial groups — variance / sampling / analysis"
            />
            {error && (
                <Alert variant="destructive" data-testid="unified-builder-error">
                    <AlertTriangle className="size-4" />
                    <AlertDescription className="font-mono text-xs">{error}</AlertDescription>
                </Alert>
            )}

            <section className="rounded-md border border-ux-border-1 bg-ux-surface-1 p-4 space-y-3">
                <h2 className="text-sm font-medium text-ux-text-1">1. Scenario & basics</h2>
                <ScenarioPickerCard value={scenarioId} onChange={setScenarioId} />
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <Label htmlFor="cname" className="text-xs">Name</Label>
                        <Input id="cname" value={name} onChange={(e) => setName(e.target.value)}
                               data-testid="unified-builder-name" />
                    </div>
                    <div>
                        <Label htmlFor="cdesc" className="text-xs">Description (optional)</Label>
                        <Input id="cdesc" value={description} onChange={(e) => setDescription(e.target.value)}
                               data-testid="unified-builder-description" />
                    </div>
                </div>
            </section>

            <section className="rounded-md border border-ux-border-1 bg-ux-surface-1 p-4 space-y-3">
                <h2 className="text-sm font-medium text-ux-text-1">2. Variance dimensions</h2>
                <ICBoundsSection
                    targetEnabled={targetEnabled} onTargetToggle={setTargetEnabled}
                    targetValue={targetBounds} onTargetChange={setTargetBounds}
                    interceptorEnabled={interEnabled} onInterceptorToggle={setInterEnabled}
                    interceptorValue={interBounds} onInterceptorChange={setInterBounds}
                />
                <div className="rounded-md border border-ux-border-1 bg-ux-surface-2/40 p-3 space-y-2">
                    <div className="flex items-center gap-2">
                        <Switch checked={headingEnabled} onCheckedChange={setHeadingEnabled}
                                data-testid="toggle-target-heading" />
                        <span className="text-sm">Target heading variance (range)</span>
                    </div>
                    {headingEnabled && (
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-[10px] text-ux-text-3">lo (deg)</Label>
                                <Input type="number" value={headingLo} onChange={(e) => setHeadingLo(e.target.value)}
                                       data-testid="heading-lo" />
                            </div>
                            <div>
                                <Label className="text-[10px] text-ux-text-3">hi (deg)</Label>
                                <Input type="number" value={headingHi} onChange={(e) => setHeadingHi(e.target.value)}
                                       data-testid="heading-hi" />
                            </div>
                        </div>
                    )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                    {[
                        ["sensor degradation", sensorDeg, setSensorDeg, "toggle-sensor-deg", null],
                        ["fuel margin", fuelMargin, setFuelMargin, "toggle-fuel-margin", null],
                        ["aero noise", aeroNoise, setAeroNoise, "toggle-aero-noise", null],
                        ["guidance-gain search (auto-locks sampling to GA)", gainSearch, onGainSearchToggle, "toggle-gain-search", "Toggling this auto-switches sampling strategy to GA — the backend rejects any other combination."],
                    ].map(([label, val, setter, tid, hint]) => (
                        <div key={label} className="flex items-center gap-2">
                            <Switch checked={val} onCheckedChange={setter} data-testid={tid} />
                            <span className="text-sm">{label}</span>
                            {hint && (
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Info className="size-3 text-ux-text-3" />
                                        </TooltipTrigger>
                                        <TooltipContent className="max-w-xs text-xs">{hint}</TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            )}
                        </div>
                    ))}
                </div>
                <p className="text-[11px] text-ux-text-3">{`Active: ${summary}`}</p>
            </section>

            <section className="rounded-md border border-ux-border-1 bg-ux-surface-1 p-4 space-y-3">
                <h2 className="text-sm font-medium text-ux-text-1">3. Sampling strategy</h2>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <Label className="text-xs">Sampler</Label>
                        <Select value={samplerKind} onValueChange={onSamplerKindChange}>
                            <SelectTrigger data-testid="sampler-kind"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="lhs">LHS (default)</SelectItem>
                                <SelectItem value="random">Random</SelectItem>
                                <SelectItem value="stratified">Stratified</SelectItem>
                                <SelectItem value="ga">GA (auto-enables guidance-gain search)</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs">N samples (max 10,000)</Label>
                        <Input type="number" value={nSamples} onChange={(e) => setNSamples(e.target.value)}
                               data-testid="n-samples" min={2} max={10000} />
                    </div>
                    <div>
                        <Label className="text-xs">MC seeds / sample</Label>
                        <Input type="number" value={mcSeeds} onChange={(e) => setMcSeeds(e.target.value)}
                               data-testid="mc-seeds" min={1} max={100} />
                    </div>
                    <div>
                        <Label className="text-xs">Seed</Label>
                        <Input type="number" value={seed} onChange={(e) => setSeed(e.target.value)}
                               data-testid="seed-input" />
                    </div>
                </div>
                {showSampleWarning && (
                    <Alert data-testid="large-n-warning"
                           className="border-amber-500/40 bg-amber-500/10 text-amber-200">
                        <AlertDescription className="text-xs">
                            Large N ({nSamples}) — wallclock scales with N × per-sample MC time / worker count.
                            Consider 4 concurrent workers ≈ 0.1 s per sample → {Math.round(nSamples * 0.1 / 4)} s.
                        </AlertDescription>
                    </Alert>
                )}
                {isGA && (
                    <div className="grid grid-cols-3 gap-3 rounded-md border border-ux-border-1 p-3">
                        <div>
                            <Label className="text-xs">GA population</Label>
                            <Input type="number" value={gaPop} onChange={(e) => setGaPop(e.target.value)}
                                   data-testid="ga-pop" />
                        </div>
                        <div>
                            <Label className="text-xs">GA generations</Label>
                            <Input type="number" value={gaGens} onChange={(e) => setGaGens(e.target.value)}
                                   data-testid="ga-gens" />
                        </div>
                        <div>
                            <Label className="text-xs">MC seeds / individual</Label>
                            <Input type="number" value={gaMc} onChange={(e) => setGaMc(e.target.value)}
                                   data-testid="ga-mc" />
                        </div>
                        <div>
                            <Label className="text-xs">Tournament size</Label>
                            <Input type="number" value={gaTournament}
                                   onChange={(e) => setGaTournament(e.target.value)}
                                   data-testid="ga-tournament" />
                        </div>
                        <div>
                            <Label className="text-xs">Crossover rate</Label>
                            <Input type="number" step="0.05" value={gaCrossover}
                                   onChange={(e) => setGaCrossover(e.target.value)}
                                   data-testid="ga-crossover" />
                        </div>
                        <div>
                            <Label className="text-xs">Mutation rate</Label>
                            <Input type="number" step="0.05" value={gaMutation}
                                   onChange={(e) => setGaMutation(e.target.value)}
                                   data-testid="ga-mutation" />
                        </div>
                        <div>
                            <Label className="text-xs">Elitism</Label>
                            <Input type="number" value={gaElitism}
                                   onChange={(e) => setGaElitism(e.target.value)}
                                   data-testid="ga-elitism" />
                        </div>
                        <div>
                            <Label className="text-xs">Early-stop patience</Label>
                            <Input type="number" value={gaEarlyStop}
                                   onChange={(e) => setGaEarlyStop(e.target.value)}
                                   data-testid="ga-early-stop" />
                        </div>
                    </div>
                )}
            </section>

            {/* Phase 17.7 — Perturbation registry + preset selector */}
            <section className="rounded-md border border-ux-border-1 bg-ux-surface-1 p-4 space-y-3">
                <h2 className="text-sm font-medium text-ux-text-1">
                    5. Perturbation registry (33-variable Monte Carlo)
                </h2>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <Label className="text-xs">Preset</Label>
                        <Select value={perturbationPreset} onValueChange={setPerturbationPreset}>
                            <SelectTrigger data-testid="perturbation-preset"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="nominal">nominal</SelectItem>
                                <SelectItem value="stressing">stressing</SelectItem>
                                <SelectItem value="worst_case">worst case</SelectItem>
                                <SelectItem value="custom">custom (JSON below)</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <div>
                    <Label className="text-xs">Per-variable overrides (JSON, optional — uses platform defaults if empty)</Label>
                    <Textarea
                        placeholder='e.g. {"sensor.detection_range_m": {"kind": "normal", "params": {"mean": 1500, "std": 75}}, "guidance.navigation_constant": {"kind": "uniform", "params": {"lo": 2.5, "hi": 4.5}}}'
                        value={perturbationOverridesJson}
                        onChange={(e) => setPerturbationOverridesJson(e.target.value)}
                        rows={6}
                        data-testid="perturbation-overrides-json"
                        className="font-mono text-xs"
                    />
                    <p className="mt-1 text-[10px] text-ux-text-3">
                        Empty = use platform defaults across all 33 variables. JSON keys must match registry IDs
                        (e.g. <code>sensor.detection_range_m</code>, <code>interceptor.max_speed_mps</code>).
                    </p>
                    <p className="mt-1 text-[10px] text-ux-text-3"
                       data-testid="perturbation-overrides-helper">
                        <strong>Note:</strong> this JSON overrides each variable&apos;s <em>distribution</em>{" "}
                        (e.g. swap a uniform for a normal, widen bounds). It does <strong>not</strong>{" "}
                        change which variables are <em>included</em>. To pick a subset of variables for
                        Sensitivity ranking or Target-Pk search, use the chip toggles inside those
                        analysis-goal sections below.
                    </p>
                </div>
            </section>

            {/* Phase 17.7 — Polar engagement envelope */}
            <section className="rounded-md border border-ux-border-1 bg-ux-surface-1 p-4 space-y-3">
                <div className="flex items-center justify-between">
                    <h2 className="text-sm font-medium text-ux-text-1">
                        6. Polar engagement envelope (range / bearing / altitude / approach)
                    </h2>
                    <div className="flex items-center gap-2">
                        <Switch checked={envelopeEnabled} onCheckedChange={setEnvelopeEnabled}
                                data-testid="toggle-polar-envelope" />
                        <span className="text-xs">enable</span>
                    </div>
                </div>
                {envelopeEnabled && (
                    <>
                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                            <div>
                                <Label className="text-xs">Range m (lo, hi)</Label>
                                <div className="flex gap-2">
                                    <Input type="number" value={envRangeLo}
                                           onChange={(e) => setEnvRangeLo(e.target.value)}
                                           data-testid="env-range-lo" />
                                    <Input type="number" value={envRangeHi}
                                           onChange={(e) => setEnvRangeHi(e.target.value)}
                                           data-testid="env-range-hi" />
                                </div>
                            </div>
                            <div>
                                <Label className="text-xs">Bearing ° (lo, hi)</Label>
                                <div className="flex gap-2">
                                    <Input type="number" value={envBearingLo}
                                           onChange={(e) => setEnvBearingLo(e.target.value)}
                                           data-testid="env-bearing-lo" />
                                    <Input type="number" value={envBearingHi}
                                           onChange={(e) => setEnvBearingHi(e.target.value)}
                                           data-testid="env-bearing-hi" />
                                </div>
                            </div>
                            <div>
                                <Label className="text-xs">Altitude m (lo, hi)</Label>
                                <div className="flex gap-2">
                                    <Input type="number" value={envAltLo}
                                           onChange={(e) => setEnvAltLo(e.target.value)}
                                           data-testid="env-alt-lo" />
                                    <Input type="number" value={envAltHi}
                                           onChange={(e) => setEnvAltHi(e.target.value)}
                                           data-testid="env-alt-hi" />
                                </div>
                            </div>
                            <div>
                                <Label className="text-xs">Approach ° (lo, hi)</Label>
                                <div className="flex gap-2">
                                    <Input type="number" value={envApproachLo}
                                           onChange={(e) => setEnvApproachLo(e.target.value)}
                                           data-testid="env-approach-lo" />
                                    <Input type="number" value={envApproachHi}
                                           onChange={(e) => setEnvApproachHi(e.target.value)}
                                           data-testid="env-approach-hi" />
                                </div>
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs">Envelope sampler</Label>
                            <Select value={envSampler} onValueChange={setEnvSampler}>
                                <SelectTrigger data-testid="env-sampler-kind"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {["uniform", "stratified", "lhs", "grid"].map((k) => (
                                        <SelectItem key={k} value={k}>{k}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </>
                )}
            </section>

            {/* Phase 17.7 — Degradation profile picker */}
            <section className="rounded-md border border-ux-border-1 bg-ux-surface-1 p-4 space-y-3">
                <h2 className="text-sm font-medium text-ux-text-1">
                    7. Degradation profile (M2 modulation pipeline)
                </h2>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <Label className="text-xs">Attach profile (optional)</Label>
                        <Select value={degradationProfileId || "__none__"}
                                onValueChange={(v) => setDegradationProfileId(v === "__none__" ? "" : v)}>
                            <SelectTrigger data-testid="degradation-profile-picker"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__none__">— none —</SelectItem>
                                {profilesList.map((p) => (
                                    <SelectItem key={p.id} value={p.id} data-testid={`profile-${p.id}`}>
                                        {p.name || p.id}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <p className="text-[10px] text-ux-text-3">
                    When attached, M2.1–M2.7 modulation rules apply to per-sample outcomes and the
                    detail page renders the modulated-outcome panel with rule activations + degraded-track
                    SHA-256 audit.
                </p>
            </section>

            <section className="rounded-md border border-ux-border-1 bg-ux-surface-1 p-4 space-y-3">
                <h2 className="text-sm font-medium text-ux-text-1">4. Analysis goals</h2>
                <div className="grid grid-cols-2 gap-3">
                    {[
                        ["descriptive Pk + KPIs", goalDescriptive, setGoalDescriptive, "goal-descriptive", null, false],
                        ["sensitivity ranking", goalSensitivity, onGoalSensitivityToggle, "goal-sensitivity", null, false],
                        ["envelope heatmap", goalEnvelope, setGoalEnvelope, "goal-envelope", null, false],
                        ["target-Pk search", goalTargetPk, onGoalTargetPkToggle, "goal-targetpk", null, false],
                        ["GA optimisation convergence", goalGA, onGoalGaToggle, "goal-ga-convergence",
                         "Enabling this auto-switches sampling to GA + enables guidance gain search — required for the GA loop to produce a convergence trace.", false],
                    ].map(([label, val, setter, tid, hint, disabled]) => (
                        <div key={label} className="flex items-center gap-2">
                            <Switch checked={val} onCheckedChange={setter} data-testid={tid}
                                    disabled={disabled} />
                            <span className={`text-sm ${disabled ? "text-ux-text-3" : ""}`}>{label}</span>
                            {hint && (
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger asChild><Info className="size-3 text-ux-text-3" /></TooltipTrigger>
                                        <TooltipContent className="max-w-xs text-xs">{hint}</TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            )}
                        </div>
                    ))}
                    {/* Phase 17.7 — Adaptive policy training permanently DEFERRED to Phase 18 */}
                    <div className="flex items-center gap-2 opacity-60"
                         data-testid="goal-adaptive-row">
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <span><Switch checked={false} disabled
                                                  data-testid="goal-adaptive-policy" /></span>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs text-xs">
                                    Adaptive policy training is on the product roadmap and not yet
                                    available in this build. The schema field remains for forward
                                    compatibility but the toggle is permanently disabled.
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                        <span className="text-sm">adaptive policy training</span>
                        <Badge variant="outline" data-testid="phase18-badge"
                               className="text-[10px] border-amber-500/40 text-amber-200">
                            Roadmap
                        </Badge>
                    </div>
                </div>
                <p className="text-[10px] text-ux-text-3">
                    Adaptive-policy training is on the product roadmap and not yet available.
                </p>
            </section>

            {/* Phase 17.7 — Sensitivity-goal sub-knobs */}
            {goalSensitivity && (
                <section className="rounded-md border border-ux-border-1 bg-ux-surface-1 p-4 space-y-3"
                         data-testid="sensitivity-config-section">
                    <h2 className="text-sm font-medium text-ux-text-1">8a. Sensitivity configuration</h2>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs">Method</Label>
                            <Select value={sensitivityMethod} onValueChange={setSensitivityMethod}>
                                <SelectTrigger data-testid="sensitivity-method-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="variance_first_order">Saltelli S1 (variance first-order)</SelectItem>
                                    <SelectItem value="one_at_a_time">One-at-a-time</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs">Samples per variable</Label>
                            <Input type="number" value={sensitivitySamplesPerVar}
                                   onChange={(e) => setSensitivitySamplesPerVar(e.target.value)}
                                   data-testid="sensitivity-samples-per-var" min={2} max={200} />
                        </div>
                    </div>

                    {/* Phase 17.9c — Variable subset chip grid (default = all 33) */}
                    {registryError ? (
                        <Alert variant="destructive"
                               data-testid="sensitivity-subset-registry-error">
                            <AlertTriangle className="size-4" />
                            <AlertDescription className="text-xs">
                                Failed to load perturbation registry: {registryError}
                            </AlertDescription>
                        </Alert>
                    ) : registryLoading && registryIds.length === 0 ? (
                        <p className="text-[11px] text-ux-text-3"
                           data-testid="sensitivity-subset-loading">
                            Loading perturbation registry…
                        </p>
                    ) : registryIds.length > 0 ? (
                        <details className="text-xs" data-testid="sensitivity-subset-details">
                            <summary className="cursor-pointer text-ux-text-2 hover:text-ux-text-1"
                                     data-testid="sensitivity-subset-toggle">
                                Customise variable subset ({sensitivityVariableSubset.length} of {registryIds.length} selected)
                            </summary>
                            <div className="mt-3 space-y-3">
                                <div className="flex gap-2">
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        className="h-7 text-[11px]"
                                        onClick={() => setSensitivityVariableSubset(registryIds)}
                                        data-testid="sensitivity-subset-select-all"
                                    >
                                        Select all
                                    </Button>
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        className="h-7 text-[11px]"
                                        onClick={() => setSensitivityVariableSubset([])}
                                        data-testid="sensitivity-subset-clear-all"
                                    >
                                        Clear all
                                    </Button>
                                </div>
                                <div className="grid grid-cols-1 gap-1 sm:grid-cols-2"
                                     data-testid="sensitivity-subset-grid">
                                    {registryIds.map((id) => {
                                        const active = sensitivityVariableSubset.includes(id);
                                        const override = perturbationOverrides[id] || { low: "", high: "" };
                                        const onRangeChange = (field, value) => {
                                            setPerturbationOverrides((prev) => ({
                                                ...prev,
                                                [id]: { ...(prev[id] || { low: "", high: "" }), [field]: value },
                                            }));
                                        };
                                        return (
                                            <div
                                                key={id}
                                                className={[
                                                    "rounded border px-2 py-1.5 transition-colors",
                                                    active
                                                        ? "border-ux-text-1 bg-ux-surface-2"
                                                        : "border-ux-border-1 bg-ux-surface-1",
                                                ].join(" ")}
                                                data-testid={`sensitivity-subset-row-${id}`}
                                            >
                                                <button
                                                    type="button"
                                                    onClick={() => toggleSensitivityVar(id)}
                                                    data-testid={`sensitivity-subset-chip-${id}`}
                                                    aria-pressed={active}
                                                    className={[
                                                        "block w-full truncate text-left text-xs transition-colors",
                                                        active ? "text-ux-text-1" : "text-ux-text-3 hover:text-ux-text-2",
                                                    ].join(" ")}
                                                >
                                                    {id}
                                                </button>
                                                {active && (
                                                    <div className="mt-1.5 flex items-center gap-1.5"
                                                         data-testid={`sensitivity-subset-range-${id}`}>
                                                        <Input
                                                            type="number"
                                                            step="any"
                                                            placeholder="min"
                                                            value={override.low}
                                                            onChange={(e) => onRangeChange("low", e.target.value)}
                                                            data-testid={`sensitivity-subset-range-min-${id}`}
                                                            className="h-6 px-1 text-[11px]"
                                                            aria-label={`Override min for ${id}`}
                                                        />
                                                        <span className="text-[10px] text-ux-text-3">–</span>
                                                        <Input
                                                            type="number"
                                                            step="any"
                                                            placeholder="max"
                                                            value={override.high}
                                                            onChange={(e) => onRangeChange("high", e.target.value)}
                                                            data-testid={`sensitivity-subset-range-max-${id}`}
                                                            className="h-6 px-1 text-[11px]"
                                                            aria-label={`Override max for ${id}`}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                                <p className="text-[10px] text-ux-text-3">
                                    Empty subset = use all {registryIds.length} variables (backend default).
                                    Leave min/max blank to use the preset's default range for that variable.
                                </p>
                            </div>
                        </details>
                    ) : null}
                </section>
            )}

            {/* Phase 17.7 — Target-Pk goal sub-knobs */}
            {goalTargetPk && (
                <section className="rounded-md border border-ux-border-1 bg-ux-surface-1 p-4 space-y-3"
                         data-testid="targetpk-config-section">
                    <h2 className="text-sm font-medium text-ux-text-1">8b. Target-Pk search</h2>
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                        <div>
                            <Label className="text-xs">Target Pk threshold</Label>
                            <Input type="number" step="0.05" value={targetPkThreshold}
                                   onChange={(e) => setTargetPkThreshold(e.target.value)}
                                   data-testid="targetpk-threshold" min={0.01} max={0.99} />
                        </div>
                        <div>
                            <Label className="text-xs">λ penalty</Label>
                            <Input type="number" step="0.05" value={targetPkLambda}
                                   onChange={(e) => setTargetPkLambda(e.target.value)}
                                   data-testid="targetpk-lambda" />
                        </div>
                        <div>
                            <Label className="text-xs">Max iterations</Label>
                            <Input type="number" value={targetPkMaxIter}
                                   onChange={(e) => setTargetPkMaxIter(e.target.value)}
                                   data-testid="targetpk-max-iter" />
                        </div>
                        <div>
                            <Label className="text-xs">Samples / iteration</Label>
                            <Input type="number" value={targetPkSamplesPerIter}
                                   onChange={(e) => setTargetPkSamplesPerIter(e.target.value)}
                                   data-testid="targetpk-samples-per-iter" />
                        </div>
                    </div>

                    {/* Phase 17.9c — Variable budget chip grid (default = all 33) */}
                    {registryError ? (
                        <Alert variant="destructive"
                               data-testid="targetpk-budget-registry-error">
                            <AlertTriangle className="size-4" />
                            <AlertDescription className="text-xs">
                                Failed to load perturbation registry: {registryError}
                            </AlertDescription>
                        </Alert>
                    ) : registryLoading && registryIds.length === 0 ? (
                        <p className="text-[11px] text-ux-text-3"
                           data-testid="targetpk-budget-loading">
                            Loading perturbation registry…
                        </p>
                    ) : registryIds.length > 0 ? (
                        <details className="text-xs" data-testid="targetpk-budget-details">
                            <summary className="cursor-pointer text-ux-text-2 hover:text-ux-text-1"
                                     data-testid="targetpk-budget-toggle">
                                Customise variable budget ({targetPkVariableBudget.length} of {registryIds.length} selected)
                            </summary>
                            <div className="mt-3 space-y-3">
                                <div className="flex gap-2">
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        className="h-7 text-[11px]"
                                        onClick={() => setTargetPkVariableBudget(registryIds)}
                                        data-testid="targetpk-budget-select-all"
                                    >
                                        Select all
                                    </Button>
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        className="h-7 text-[11px]"
                                        onClick={() => setTargetPkVariableBudget([])}
                                        data-testid="targetpk-budget-clear-all"
                                    >
                                        Clear all
                                    </Button>
                                </div>
                                <div className="grid grid-cols-2 gap-1 sm:grid-cols-3"
                                     data-testid="targetpk-budget-grid">
                                    {registryIds.map((id) => {
                                        const active = targetPkVariableBudget.includes(id);
                                        return (
                                            <button
                                                key={id}
                                                type="button"
                                                onClick={() => toggleTargetPkVar(id)}
                                                data-testid={`targetpk-budget-chip-${id}`}
                                                aria-pressed={active}
                                                className={[
                                                    "truncate rounded border px-2 py-1 text-left transition-colors",
                                                    active
                                                        ? "border-ux-text-1 bg-ux-surface-2 text-ux-text-1"
                                                        : "border-ux-border-1 bg-ux-surface-1 text-ux-text-3 hover:bg-ux-surface-2/60",
                                                ].join(" ")}
                                            >
                                                {id}
                                            </button>
                                        );
                                    })}
                                </div>
                                <p className="text-[10px] text-ux-text-3">
                                    Empty budget = backend falls back to the default top-10 variable budget.
                                </p>
                            </div>
                        </details>
                    ) : null}
                </section>
            )}

            <div className="space-y-2"
                 data-testid="unified-builder-validation-block">
                {lastAutoFix && (
                    <Alert data-testid="unified-builder-autofix"
                           className="border-sky-500/40 bg-sky-500/10 text-sky-200">
                        <Info className="size-4" />
                        <AlertDescription className="text-xs">
                            {lastAutoFix}
                        </AlertDescription>
                    </Alert>
                )}
                {validation.errors.length > 0 && (
                    <Alert variant="destructive"
                           data-testid="unified-builder-validation-summary">
                        <AlertCircle className="size-4" />
                        <AlertDescription>
                            <div className="font-semibold mb-1">
                                {validation.errors.length} issue{validation.errors.length === 1 ? "" : "s"} block submit
                            </div>
                            <ul className="space-y-1 text-xs">
                                {validation.errors.map((e) => (
                                    <li key={e.id}
                                        data-testid={`validation-error-${e.id}`}
                                        className="flex items-start gap-2">
                                        <span aria-hidden>•</span>
                                        <span>{e.message}</span>
                                    </li>
                                ))}
                            </ul>
                        </AlertDescription>
                    </Alert>
                )}
                {validation.warnings.length > 0 && (
                    <Alert data-testid="unified-builder-validation-warnings"
                           className="border-amber-500/40 bg-amber-500/10 text-amber-200">
                        <AlertTriangle className="size-4" />
                        <AlertDescription>
                            <div className="font-semibold mb-1">
                                {validation.warnings.length} non-blocking warning{validation.warnings.length === 1 ? "" : "s"}
                            </div>
                            <ul className="space-y-1 text-xs">
                                {validation.warnings.map((w) => (
                                    <li key={w.id}
                                        data-testid={`validation-warning-${w.id}`}
                                        className="flex items-start gap-2">
                                        <span aria-hidden>•</span><span>{w.message}</span>
                                    </li>
                                ))}
                            </ul>
                        </AlertDescription>
                    </Alert>
                )}
                {validation.errors.length === 0 && validation.warnings.length === 0 && (
                    <Alert data-testid="unified-builder-validation-clean"
                           className="border-emerald-500/40 bg-emerald-500/10 text-emerald-200">
                        <CheckCircle2 className="size-4" />
                        <AlertDescription className="text-xs">
                            All cross-field rules satisfied — ready to submit.
                        </AlertDescription>
                    </Alert>
                )}
            </div>

            <div className="flex gap-3 justify-end">
                <Button variant="ghost" onClick={() => navigate("/campaigns")}
                        data-testid="unified-builder-cancel">Cancel</Button>
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span>
                                <Button onClick={submit}
                                        disabled={busy || !scenarioId || hasErrors}
                                        data-testid="unified-builder-submit">
                                    {busy
                                        ? <><Loader2 className="mr-1 size-3 animate-spin" /> Submitting…</>
                                        : "Create campaign"}
                                </Button>
                            </span>
                        </TooltipTrigger>
                        {hasErrors && (
                            <TooltipContent className="text-xs">
                                {validation.errors.length} unresolved issue
                                {validation.errors.length === 1 ? "" : "s"} — see summary above.
                            </TooltipContent>
                        )}
                    </Tooltip>
                </TooltipProvider>
            </div>
        </div>
    );
}
